import type { IdentityDmMessage } from "./directMessaging";

export type LocalMessageStatus = "sending" | "failed";

export type OptimisticIdentityDmMessage = IdentityDmMessage & {
  localStatus?: LocalMessageStatus;
};

export function normalizeDirectMessageText(value: string) {
  return value.trim();
}

function isLocalOnly(message: OptimisticIdentityDmMessage) {
  return message.localStatus === "sending" || message.localStatus === "failed";
}

/**
 * Server records always win over their local optimistic counterpart. This is
 * deliberately keyed by the client-generated Firestore document ID.
 */
export function mergeIdentityDmMessages(
  current: readonly OptimisticIdentityDmMessage[],
  incoming: readonly OptimisticIdentityDmMessage[],
) {
  const messages = new Map(current.map((message) => [message.id, message]));

  for (const message of incoming) {
    const existing = messages.get(message.id);
    if (!existing || isLocalOnly(existing) || !isLocalOnly(message)) {
      messages.set(message.id, message);
    }
  }

  return [...messages.values()].sort((left, right) => {
    if (left.createdAt !== right.createdAt) return left.createdAt - right.createdAt;
    return left.id.localeCompare(right.id);
  });
}

export function optimisticIdentityDmMessage(input: {
  messageId: string;
  senderIdentityId: string;
  senderSnapshot: IdentityDmMessage["senderSnapshot"];
  text: string;
  createdAt: number;
}): OptimisticIdentityDmMessage {
  return {
    id: input.messageId,
    senderIdentityId: input.senderIdentityId,
    senderSnapshot: input.senderSnapshot,
    type: "text",
    text: input.text,
    createdAt: input.createdAt,
    localStatus: "sending",
  };
}

export function updateLocalMessageStatus(
  messages: readonly OptimisticIdentityDmMessage[],
  messageId: string,
  localStatus: LocalMessageStatus,
) {
  return messages.map((message) => (
    message.id === messageId ? { ...message, localStatus } : message
  ));
}

export function canClaimDraftSubmission(input: {
  text: string;
  draftVersion: number;
  lastSubmittedVersion: number | null;
}) {
  return normalizeDirectMessageText(input.text).length > 0
    && input.draftVersion !== input.lastSubmittedVersion;
}

export function shouldSubmitComposerKey(input: {
  key?: string;
  shiftKey?: boolean;
  isComposing?: boolean;
}) {
  return input.key === "Enter" && !input.shiftKey && !input.isComposing;
}
